<template>
  <div class="team-hotel-access">
    <div
      class="d-flex align-center flex-wrap px-4 pt-4 pb-3"
      style="gap: 16px;"
    >
      <div class="flex-grow-1">
        <div class="title font-weight-medium">
          {{ $t("which_establishments_can_see") }}
        </div>
        <div class="body-2 grey--text text--darken-1">
          {{
            $t("team_hotels_with_access", {
              n: access_count,
              total: hotel_list.length
            })
          }}
        </div>
      </div>
      <v-text-field
        v-model="query"
        :placeholder="$t('team_hotels_search')"
        :aria-label="$t('team_hotels_search')"
        prepend-inner-icon="mdi-magnify"
        outlined
        rounded
        dense
        clearable
        hide-details
        style="max-width: 280px;"
      ></v-text-field>
    </div>

    <div class="tha-row tha-head">
      <div>{{ $t("team_hotels_access") }}</div>
      <div>{{ $t("team_hotels_establishment") }}</div>
      <div>{{ $t("team_hotels_type") }}</div>
      <div>{{ $t("team_hotels_import_emails") }}</div>
      <div></div>
    </div>

    <div
      v-for="row in rows"
      :key="row.hotel.id"
      class="tha-row tha-item"
      :class="{ 'tha-off': !row.hotel.has_access }"
    >
      <div>
        <v-switch
          v-model="row.hotel.has_access"
          color="secondary"
          class="ma-0 pa-0"
          dense
          hide-details
          :aria-label="row.hotel.name"
        ></v-switch>
      </div>
      <div class="d-flex align-center" style="min-width: 0;">
        <span
          :style="{ width: row.depth * 24 + 'px' }"
          class="flex-shrink-0"
        ></span>
        <v-btn
          v-if="row.is_group"
          icon
          x-small
          class="mr-2"
          :aria-label="row.hotel.name"
          :aria-expanded="row.expanded ? 'true' : 'false'"
          @click="toggleGroup(row.hotel.id)"
        >
          <v-icon small>{{
            row.expanded ? "mdi-chevron-down" : "mdi-chevron-right"
          }}</v-icon>
        </v-btn>
        <span v-else class="tha-chev-space flex-shrink-0"></span>
        <span
          class="tha-name text-truncate"
          :class="{ 'tha-group': row.is_group }"
          :title="row.hotel.name"
          >{{ row.hotel.name }}</span
        >
        <span v-if="row.is_group" class="tha-muted ml-2 text-no-wrap">{{
          $tc("team_hotels_n_elements", row.children, { n: row.children })
        }}</span>
      </div>
      <div class="tha-muted">{{ typeLabel(row) }}</div>
      <div
        class="tha-summary text-truncate"
        :class="{
          'tha-summary-empty': row.hotel.has_access && !emailCount(row.hotel)
        }"
      >
        <template v-if="row.hotel.has_access">{{
          emailSummary(row.hotel)
        }}</template>
      </div>
      <div class="text-right">
        <v-btn
          v-if="row.hotel.has_access && hasTasks(row.hotel)"
          icon
          small
          color="grey darken-1"
          :title="$t('team_hotels_configure_emails')"
          :aria-label="
            $t('team_hotels_configure_emails') + ' · ' + row.hotel.name
          "
          @click="openEmails(row.hotel)"
        >
          <v-icon small>mdi-email-outline</v-icon>
        </v-btn>
      </div>
    </div>

    <div v-if="!rows.length" class="pa-6 body-2 grey--text text--darken-1">
      {{ $t("team_hotels_no_results") }}
    </div>

    <v-dialog
      v-model="drawer"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="460"
      scrollable
    >
      <v-card v-if="drawer_hotel">
        <v-card-title class="headline justify-center text-center">
          <div>
            <div>{{ drawer_hotel.name }}</div>
            <div v-if="drawer_path" class="caption grey--text text--darken-1">
              {{ drawer_path }}
            </div>
          </div>
          <v-btn
            style="position:absolute; top:13px; right:10px;"
            icon
            :aria-label="$t('close')"
            @click="drawer = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text class="white">
          <div class="body-2 grey--text text--darken-2 mb-2">
            {{ $t("team_hotels_import_emails_hint") }}
          </div>
          <div
            v-for="(task, task_code) in drawer_hotel.tasks"
            :key="task_code"
            class="py-3"
          >
            <div class="subtitle-2 mb-2">{{ $t(`task-${task_code}`) }}</div>
            <v-select
              :value="drawer_hotel.email_template[task_code]"
              :items="emailTemplates(drawer_hotel)"
              outlined
              rounded
              dense
              hide-details
              color="secondary"
              @change="setTemplate(drawer_hotel, task_code, $event)"
            ></v-select>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4 px-6">
          <v-btn
            color="secondary"
            rounded
            depressed
            class="px-5"
            @click="drawer = false"
          >
            {{ $t("team_hotels_done") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Llista d'establiments d'un usuari de l'equip: accés per hotel i plantilla
// d'email per a cada importació. Modifica directament `has_access` i
// `email_template` dels objectes de `hotels`, igual que abans al Team.vue.
export default {
  props: {
    // Objecte indexat per l'"order" del backend; l'arbre es construeix amb
    // father_id (l'order conté els noms i no es pot partir pels guions).
    hotels: { type: Object, default: () => ({}) },
    // hotel => items del v-select de plantilles
    emailTemplates: { type: Function, required: true }
  },
  data: () => ({
    query: "",
    collapsed: {},
    drawer: false,
    drawer_hotel: null
  }),
  computed: {
    hotel_list() {
      return Object.values(this.hotels || {});
    },
    hotel_by_id() {
      const map = {};
      this.hotel_list.forEach(h => (map[h.id] = h));
      return map;
    },
    children_by_father() {
      const map = {};
      const byName = (a, b) =>
        String(a.name).localeCompare(String(b.name), undefined, {
          sensitivity: "base"
        });
      this.hotel_list.forEach(h => {
        const father = this.hotel_by_id[h.father_id] ? h.father_id : 0;
        (map[father] = map[father] || []).push(h);
      });
      Object.keys(map).forEach(k => {
        const list = map[k];
        map[k] = list
          .filter(h => this.isGroup(h))
          .sort(byName)
          .concat(list.filter(h => !this.isGroup(h)).sort(byName));
      });
      return map;
    },
    normalized_query() {
      return this.normalize(this.query || "").trim();
    },
    rows() {
      const q = this.normalized_query;
      const rows = [];
      const matches = h =>
        !q ||
        this.normalize(h.name).includes(q) ||
        (this.children_by_father[h.id] || []).some(matches);
      const walk = (father, depth) => {
        (this.children_by_father[father] || []).forEach(h => {
          if (!matches(h)) return;
          const is_group = this.isGroup(h);
          const expanded = !!q || !this.collapsed[h.id];
          rows.push({
            hotel: h,
            depth,
            is_group,
            expanded,
            children: (this.children_by_father[h.id] || []).length
          });
          if (is_group && expanded) walk(h.id, depth + 1);
        });
      };
      walk(0, 0);
      return rows;
    },
    access_count() {
      return this.hotel_list.filter(h => h.has_access).length;
    },
    drawer_path() {
      const names = [];
      let father = this.drawer_hotel
        ? this.hotel_by_id[this.drawer_hotel.father_id]
        : null;
      while (father && names.length < 20) {
        names.unshift(father.name);
        father = this.hotel_by_id[father.father_id];
      }
      return names.join(" › ");
    }
  },
  methods: {
    normalize(text) {
      return String(text)
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase();
    },
    isGroup(hotel) {
      return hotel.children_number > 0;
    },
    hasTasks(hotel) {
      return !!hotel.tasks && Object.keys(hotel.tasks).length > 0;
    },
    typeLabel(row) {
      if (row.is_group) {
        return this.$t(
          row.depth === 0 ? "team_hotels_group" : "team_hotels_subgroup"
        );
      }
      return this.$t(
        row.hotel.type === "hotel" ? "hotel" : "team_hotels_restaurant"
      );
    },
    toggleGroup(id) {
      this.$set(this.collapsed, id, !this.collapsed[id]);
    },
    configuredTasks(hotel) {
      const templates = hotel.email_template || {};
      return Object.keys(hotel.tasks || {}).filter(code => templates[code]);
    },
    emailCount(hotel) {
      return this.configuredTasks(hotel).length;
    },
    emailSummary(hotel) {
      if (!this.hasTasks(hotel)) return "";
      const tasks = this.configuredTasks(hotel);
      if (!tasks.length) return this.$t("team_hotels_no_emails");
      if (tasks.length > 1) {
        return this.$t("team_hotels_n_emails", { n: tasks.length });
      }
      const value = hotel.email_template[tasks[0]];
      const template = this.emailTemplates(hotel).find(t => t.value === value);
      return (
        (template ? template.text : value) + " · " + this.$t(`task-${tasks[0]}`)
      );
    },
    setTemplate(hotel, task_code, value) {
      if (!hotel.email_template || typeof hotel.email_template !== "object") {
        this.$set(hotel, "email_template", {});
      }
      this.$set(hotel.email_template, task_code, value);
    },
    openEmails(hotel) {
      this.drawer_hotel = hotel;
      this.drawer = true;
    }
  }
};
</script>

<style scoped lang="scss">
.tha-row {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr) 120px minmax(0, 300px) 48px;
  align-items: center;
  padding: 0 16px;
}
.tha-head {
  height: 32px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #6b7480;
  background: #fafbfc;
  border-top: 1px solid #e3e8ed;
  border-bottom: 1px solid #e3e8ed;
}
.tha-item {
  height: 44px;
  border-bottom: 1px solid #eef1f4;
  &:hover {
    background: #f5f7f9;
  }
}
.tha-name {
  font-size: 15px;
  font-weight: 500;
  color: #1d2733;
}
.tha-group {
  font-weight: 600;
}
.tha-off .tha-name {
  color: #6b7480;
  font-weight: 400;
}
.tha-off .tha-group {
  font-weight: 600;
}
.tha-chev-space {
  width: 28px;
}
.tha-muted {
  font-size: 13px;
  color: #6b7480;
}
.tha-summary {
  font-size: 13px;
  color: #4a5560;
}
.tha-summary-empty {
  color: #8a939c;
}
@media (max-width: 700px) {
  .tha-row {
    grid-template-columns: 56px minmax(0, 1fr) 48px;
  }
  .tha-row > :nth-child(3),
  .tha-row > :nth-child(4) {
    display: none;
  }
}
</style>
